import {
  createFrontendModule,
  PageLayout,
  type PageLayoutProps,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { PluginHeader, type HeaderTab } from '@backstage/ui';
import { useMemo } from 'react';
import {
  useInRouterContext,
  useLocation,
  useResolvedPath,
} from 'react-router-dom';
import { useVisibleTabs } from './restrictedTabs';

const WIZARD_PATH_RE = /\/create\/templates\/[^/]+\/[^/]+/;
const ENTITY_PATH_RE = /^\/catalog\/[^/]+\/[^/]+\/[^/]+/;

type BasePageLayoutProps = Omit<PageLayoutProps, 'tabs'> & {
  tabs?: HeaderTab[];
};

const BasePageLayout = ({
  title,
  icon,
  titleLink,
  headerActions,
  tabs,
  children,
}: BasePageLayoutProps) => (
  <>
    <PluginHeader
      title={title}
      icon={icon}
      titleLink={titleLink}
      tabs={tabs}
      customActions={headerActions}
    />
    {children}
  </>
);

const RoutedPageLayout = (props: PageLayoutProps) => {
  const { pathname } = useLocation();
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const visibleTabs = useVisibleTabs(props.tabs);
  const tabs = useMemo(
    () =>
      visibleTabs?.map(tab => ({
        ...tab,
        href: tab.href.startsWith('/')
          ? tab.href
          : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
        matchStrategy: 'prefix' as const,
      })),
    [visibleTabs, parentPath],
  );

  if (
    props.noHeader ||
    WIZARD_PATH_RE.test(pathname) ||
    ENTITY_PATH_RE.test(pathname)
  ) {
    return <>{props.children}</>;
  }

  return <BasePageLayout {...props} tabs={tabs} />;
};

const KaaPPageLayout = (props: PageLayoutProps) => {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <RoutedPageLayout {...props} />
  ) : (
    <BasePageLayout {...props} tabs={undefined} />
  );
};

export const scaffolderChromeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SwappableComponentBlueprint.make({
      name: 'page-layout',
      params: defineParams =>
        defineParams({
          component: PageLayout,
          loader: () => KaaPPageLayout,
        }),
    }),
  ],
});
